import { useState } from 'react';
import { isoVersDate } from '../../api/mappers';
import { Bar } from '../../components/ui/Bar.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { TYPES_TVA, cleDate, historiqueTva, tauxLu } from '../../lib/tva';
import { C } from '../../theme/palette';
import { tr, trf } from '../../lib/i18n';

const titre = {
  fontSize: 9,
  fontWeight: 700,
  color: C.muted,
  marginBottom: 5,
  textTransform: 'uppercase',
};
const aide = { fontSize: 8, color: C.muted, lineHeight: 1.5 };
const champ = {
  textAlign: 'center',
  fontWeight: 800,
  fontSize: 14,
  border: '1px solid ' + C.border,
  borderRadius: 9,
  padding: 8,
  color: C.ink,
  width: 64,
};
const bouton = {
  padding: '7px 11px',
  borderRadius: 8,
  fontSize: 10,
  fontWeight: 700,
  cursor: 'pointer',
  border: '1px solid ' + C.border,
  background: C.white,
  color: C.navy,
};

/** La veille d'une date « AAAA-MM-JJ », écrite « JJ.MM.AAAA ». */
function veille(iso) {
  const d = new Date(iso + 'T12:00:00');
  d.setDate(d.getDate() - 1);
  return isoVersDate(cleDate(d));
}

/** Le prochain 1er janvier : la date habituelle d'un changement de taux. */
function prochainPremierJanvier() {
  return `${new Date().getFullYear() + 1}-01-01`;
}

/**
 * Champ d'un taux, qui garde le texte tapé : lié au seul nombre, il avalait
 * la virgule de « 8,1 » dès la première frappe. Une saisie illisible n'écrit
 * rien ; le dernier taux valable reste enregistré.
 */
function ChampTaux({ libelle, valeur, surValeur }) {
  const [texte, setTexte] = useState(String(valeur));
  return (
    <input
      aria-label={libelle}
      value={texte}
      inputMode="decimal"
      onChange={(e) => {
        setTexte(e.target.value);
        const n = tauxLu(e.target.value);
        if (n != null) surValeur(n);
      }}
      style={champ}
    />
  );
}

/**
 * Les trois taux légaux de TVA et leur historique (Réglages → Documents).
 *
 * <p>Les taux changent avec les années — 7,7 % jusqu'à fin 2023, 8,1 % depuis
 * — et ils étaient écrits en dur dans les écrans. Ils se règlent ici, chacun
 * avec sa date d'entrée en vigueur : les documents déjà établis gardent leur
 * taux, les nouveaux prennent celui en vigueur à leur date, et le décompte
 * applique à chaque pièce le taux de son époque. Quand la loi change, un
 * bouton ajoute la période suivante ; rien d'autre à reprendre.
 *
 * <p>La carte travaille sur l'historique tel que `lib/tva.js` le lit : une
 * période mal écrite par une version ancienne disparaît de l'affichage comme
 * du calcul, et chaque modification réécrit un historique entier et valable.
 *
 * @param {object} props propriétés du composant
 * @param {object} props.soc entreprise
 * @param {(patch: object) => void} props.set applique une modification à l'entreprise
 * @returns {import('react').ReactElement} la carte
 */
export function TauxTvaCard({ soc, set }) {
  const periodes = historiqueTva(soc);
  const aujourdhui = cleDate(new Date());
  const [dateAjout, setDateAjout] = useState(prochainPremierJanvier());
  const dejaConnue = periodes.some((p) => p.des === dateAjout);

  const ecrit = (nouvelles) =>
    set({ tvaHistorique: [...nouvelles].sort((a, b) => a.des.localeCompare(b.des)) });
  const modifie = (des, cle, valeur) =>
    ecrit(periodes.map((p) => (p.des === des ? { ...p, [cle]: valeur } : p)));
  const retire = (des) => ecrit(periodes.filter((p) => p.des !== des));
  const ajoute = () => {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(dateAjout) || dejaConnue) return;
    const derniere = periodes[periodes.length - 1];
    ecrit([...periodes, { ...derniere, des: dateAjout }]);
  };

  /**
   * « En vigueur dès le … », « À venir dès le … », « Du … au … » — et, quand
   * un changement est planifié, « En vigueur du … au … » pour la période du
   * jour : elle ne devenait qu'une période parmi d'autres dès qu'on ajoutait
   * la suivante, et l'écran ne disait plus quels taux s'appliquent aujourd'hui.
   */
  const libelle = (p, suivante) => {
    const debut = p.des ? isoVersDate(p.des) : null;
    if (suivante && (p.des || '') <= aujourdhui && suivante.des > aujourdhui) {
      return debut
        ? trf('En vigueur du {0} au {1}', debut, veille(suivante.des))
        : trf('En vigueur jusqu’au {0}', veille(suivante.des));
    }
    if (suivante)
      return debut
        ? trf('Du {0} au {1}', debut, veille(suivante.des))
        : trf('Jusqu’au {0}', veille(suivante.des));
    if (p.des > aujourdhui) return trf('À venir dès le {0}', debut);
    return debut ? trf('En vigueur dès le {0}', debut) : tr('En vigueur');
  };

  return (
    <Card style={{ marginBottom: 11 }}>
      <Bar
        icon="🧾"
        title="Taux de TVA"
        color={C.teal}
        sub="Normal, réduit, hébergement — avec leur date d'entrée en vigueur"
      />
      <div style={{ padding: 13 }}>
        {[...periodes].reverse().map((p, i, liste) => {
          const suivante = liste[i - 1];
          const courante = !suivante && p.des <= aujourdhui;
          return (
            <div
              key={p.des}
              style={{
                border: '1px solid ' + (courante ? C.teal : C.border),
                background: courante ? C.tealL : C.white,
                borderRadius: 10,
                padding: '9px 11px',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  marginBottom: 7,
                }}
              >
                <div style={{ fontSize: 10.5, fontWeight: 800, color: courante ? C.teal : C.navy }}>
                  {libelle(p, suivante)}
                </div>
                {periodes.length > 1 && (
                  <button
                    type="button"
                    aria-label={trf('Retirer la période {0}', libelle(p, suivante))}
                    onClick={() => retire(p.des)}
                    style={{ ...bouton, color: C.red, padding: '4px 8px' }}
                  >
                    Retirer
                  </button>
                )}
              </div>
              <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {TYPES_TVA.map((t) => (
                  <label key={t.cle} style={{ display: 'block' }}>
                    <div style={titre}>{t.libelle}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                      <ChampTaux
                        libelle={`${tr(t.libelle)} ${libelle(p, suivante)}`}
                        valeur={p[t.cle]}
                        surValeur={(n) => modifie(p.des, t.cle, n)}
                      />
                      <span style={{ fontSize: 12, fontWeight: 800, color: C.grey }}>%</span>
                    </div>
                  </label>
                ))}
              </div>
            </div>
          );
        })}

        <div style={{ ...titre, marginTop: 11 }}>Changement de taux</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <input
            type="date"
            aria-label="Date d'entrée en vigueur des nouveaux taux"
            value={dateAjout}
            onChange={(e) => setDateAjout(e.target.value)}
            style={{ ...champ, width: 'auto', fontSize: 11, fontWeight: 700, textAlign: 'left' }}
          />
          <button type="button" onClick={ajoute} disabled={dejaConnue} style={bouton}>
            Ajouter un changement de taux dès le {isoVersDate(dateAjout) || '…'}
          </button>
        </div>
        <div style={{ ...aide, marginTop: 8 }}>
          Les nouveaux taux partent de ceux en vigueur : corrige-les ensuite. Les documents déjà
          établis gardent leur taux ; les nouveaux, les tickets de caisse et le décompte prennent
          celui en vigueur à leur date. Le taux réduit vaut pour les denrées alimentaires, les
          boissons sans alcool, les livres et les médicaments ; le taux spécial pour l'hébergement.
        </div>
      </div>
    </Card>
  );
}
